import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ShieldAlert, Zap, OctagonX, Ban, Shield, AlertTriangle, Save, RefreshCw } from "lucide-react";

export default function RiskControlsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const r = await api.get("/risk");
      setSettings(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await api.put("/risk", settings);
      setSettings(r.data);
      toast.success("Risk settings updated");
    } catch (e) { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const triggerEmergencyBrake = async () => {
    if (!window.confirm("EMERGENCY BRAKE: This will stop ALL bots and cancel ALL open orders. Continue?")) return;
    try {
      await api.post("/risk/emergency-brake");
      toast.success("Emergency brake activated");
      fetchSettings();
    } catch (e) { toast.error("Emergency brake failed"); }
  };

  const deactivateEmergencyBrake = async () => {
    if (!window.confirm("Are you sure you want to DEACTIVATE the emergency brake? This will allow bots to run again.")) return;
    try {
      await api.post("/risk/deactivate-brake");
      toast.success("Emergency brake deactivated");
      fetchSettings();
    } catch (e) { toast.error("Failed to deactivate brake"); }
  };

  const cancelAllOrders = async () => {
    if (!window.confirm("Cancel ALL open orders on MEXC?")) return;
    try {
      const r = await api.post("/risk/cancel-all");
      toast.success(`Cancelled ${r.data.cancelled || 0} orders`);
    } catch (e) { toast.error("Failed to cancel orders"); }
  };

  const up = (key, val) => setSettings((p) => ({ ...p, [key]: val }));

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-lg" />)}</div>;

  const s = settings || {};

  return (
    <div className="space-y-6" data-testid="risk-controls-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Risk Controls</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Configure kill switches and safety limits</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchSettings} data-testid="refresh-risk-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-2" />Refresh
          </Button>
          <Button size="sm" onClick={saveSettings} disabled={saving} data-testid="save-risk-btn">
            <Save className="w-3.5 h-3.5 mr-2" />{saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Emergency Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className={`border-red-500/30 ${s.emergency_brake ? "bg-red-500/10 glow-danger" : "bg-card"}`}>
          <CardContent className="py-6 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <OctagonX className="w-8 h-8 text-red-500" />
              <div>
                <p className="font-bold text-red-400">Emergency Brake</p>
                <p className="text-xs text-muted-foreground">Stop all bots + cancel all orders</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {!s.emergency_brake ? (
                <Button className="kill-switch-btn flex-1" onClick={triggerEmergencyBrake} data-testid="emergency-brake-btn">
                  <Zap className="w-4 h-4 mr-2" />ACTIVATE
                </Button>
              ) : (
                <>
                  <Button className="kill-switch-btn flex-1" disabled>
                    <OctagonX className="w-4 h-4 mr-2" />BRAKE ACTIVE
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" 
                    onClick={deactivateEmergencyBrake} 
                    data-testid="deactivate-brake-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />Deactivate
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-amber-500/30">
          <CardContent className="py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Ban className="w-8 h-8 text-amber-500" />
              <div>
                <p className="font-bold text-amber-400">Cancel All Orders</p>
                <p className="text-xs text-muted-foreground">Cancel all open orders on MEXC</p>
              </div>
            </div>
            <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10" onClick={cancelAllOrders} data-testid="cancel-all-btn">
              Cancel All
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Status */}
      <div className="flex gap-3">
        <Badge className={s.emergency_brake ? "bg-red-500/15 text-red-500 border-transparent" : "bg-emerald-500/15 text-emerald-500 border-transparent"}>
          {s.emergency_brake ? "BRAKE ACTIVE" : "System Normal"}
        </Badge>
        <Badge className={s.safe_mode ? "bg-blue-500/15 text-blue-400 border-transparent" : "bg-secondary text-muted-foreground"}>
          Safe Mode: {s.safe_mode ? "ON" : "OFF"}
        </Badge>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cost Limits */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />Cost & Loss Limits
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider">Max Cost (USDT)</Label>
              <Input type="number" value={s.max_cost_usdt ?? ""} onChange={(e) => up("max_cost_usdt", parseFloat(e.target.value) || 0)} className="bg-secondary/50 font-mono" data-testid="risk-max-cost" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider">Max Loss (USDT)</Label>
              <Input type="number" value={s.max_loss_usdt ?? ""} onChange={(e) => up("max_loss_usdt", parseFloat(e.target.value) || 0)} className="bg-secondary/50 font-mono" data-testid="risk-max-loss" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider">Daily Loss Limit (USDT)</Label>
              <Input type="number" value={s.daily_loss_limit_usdt ?? ""} onChange={(e) => up("daily_loss_limit_usdt", parseFloat(e.target.value) || 0)} className="bg-secondary/50 font-mono" data-testid="risk-daily-loss" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider">Session Loss Limit (USDT)</Label>
              <Input type="number" value={s.session_loss_limit_usdt ?? ""} onChange={(e) => up("session_loss_limit_usdt", parseFloat(e.target.value) || 0)} className="bg-secondary/50 font-mono" data-testid="risk-session-loss" />
            </div>
          </CardContent>
        </Card>

        {/* Inventory & Price */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />Inventory & Price Limits
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider">Max Inventory (Base Units)</Label>
              <Input type="number" value={s.max_inventory_base ?? ""} onChange={(e) => up("max_inventory_base", parseFloat(e.target.value) || 0)} className="bg-secondary/50 font-mono" data-testid="risk-max-inv-base" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider">Max Inventory (USDT Notional)</Label>
              <Input type="number" value={s.max_inventory_usdt ?? ""} onChange={(e) => up("max_inventory_usdt", parseFloat(e.target.value) || 0)} className="bg-secondary/50 font-mono" data-testid="risk-max-inv-usdt" />
            </div>
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <Label className="text-[10px] uppercase tracking-wider">Price Band (%)</Label>
                <span className="font-mono text-sm text-primary">{s.price_band_pct?.toFixed(1)}%</span>
              </div>
              <Slider value={[s.price_band_pct || 5]} onValueChange={([v]) => up("price_band_pct", v)} min={0.5} max={20} step={0.5} className="py-2" data-testid="risk-price-band" />
              <p className="text-[10px] text-muted-foreground">Do not quote outside this % band vs mid price</p>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-xs font-medium">Safe Mode</Label>
                <p className="text-[10px] text-muted-foreground">Avoid crossing top-of-book</p>
              </div>
              <Switch checked={s.safe_mode || false} onCheckedChange={(v) => up("safe_mode", v)} data-testid="risk-safe-mode" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
